import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import AppSidebar from '@/components/AppSidebar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Shield, ShieldAlert, Lock, Leaf, Activity, MapPin, Menu, LogOut, ListTodo,
  BarChart3, RefreshCw, Play, AlertTriangle, Eye, Home, Wifi, FileCheck, FileDown,
  SlidersHorizontal, RotateCcw, CheckCircle2, Wrench
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVEL_STYLE = {
  Critical: 'bg-red-100 text-red-800 border border-red-300',
  Warning: 'bg-orange-100 text-orange-800 border border-orange-300',
  Anomaly: 'bg-yellow-100 text-yellow-800 border border-yellow-300',
  Normal: 'bg-green-100 text-green-700 border border-green-200',
  High: 'bg-orange-100 text-orange-800 border border-orange-300',
};

const ALARM_STATES = ['Open', 'Unlocked', 'Breach', 'Degraded', 'Offline', 'In Transit', 'Moving'];

const TABS = [
  { key: 'physical', label: 'Physical Security', icon: Lock },
  { key: 'damage', label: 'Physical Damage', icon: Home },
  { key: 'environmental', label: 'Environmental', icon: Leaf },
  { key: 'incidents', label: 'Incidents', icon: AlertTriangle },
  { key: 'compliance', label: 'Compliance', icon: FileCheck },
];

const NavButton = ({ icon: Icon, label, onClick, active, open }) => (
  <Button variant="ghost" onClick={onClick}
    className={`w-full justify-start ${active ? 'bg-red-50 text-red-700' : 'text-gray-700 hover:text-black hover:bg-gray-100'}`}>
    <Icon className="w-5 h-5" />{open && <span className="ml-3 text-left">{label}</span>}
  </Button>
);

const statusPill = (status) => {
  const alarm = ALARM_STATES.includes(status);
  const warn = status === 'Degraded';
  return `text-xs px-2 py-0.5 rounded font-medium ${alarm ? (warn ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700') : 'bg-green-100 text-green-700'}`;
};

export default function PhysicalSecurityPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const isEnvRoute = location.pathname.startsWith('/environmental');
  const initialTab = (isEnvRoute || params.get('tab') === 'environmental') ? 'environmental' : 'physical';

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [tab, setTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const [user] = useState(JSON.parse(localStorage.getItem('user') || '{}'));

  const [summary, setSummary] = useState(null);
  const [sensors, setSensors] = useState([]);
  const [structural, setStructural] = useState([]);
  const [env, setEnv] = useState({ signals: [], alerts: [] });
  const [incidents, setIncidents] = useState([]);
  const [compliance, setCompliance] = useState([]);
  const [overrideOptions, setOverrideOptions] = useState({});
  const mounted = useRef(true);

  const token = localStorage.getItem('token');
  const config = { headers: { Authorization: `Bearer ${token}` } };
  const isGuest = user?.role === 'guest';

  const loadAll = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [s, sen, st, e, inc, comp] = await Promise.all([
        axios.get(`${API}/domain/summary`, config),
        axios.get(`${API}/domain/sensors`, config),
        axios.get(`${API}/domain/structural`, config),
        axios.get(`${API}/domain/environmental`, config),
        axios.get(`${API}/domain/incidents`, config),
        axios.get(`${API}/domain/compliance`, config),
      ]);
      setSummary(s.data); setSensors(sen.data); setStructural(st.data);
      setEnv(e.data); setIncidents(inc.data); setCompliance(comp.data);
    } catch (err) {
      if (err.response?.status === 401) { localStorage.clear(); navigate('/login'); }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { mounted.current = true; loadAll(); return () => { mounted.current = false; }; }, [loadAll]);

  useEffect(() => {
    setTab(isEnvRoute ? 'environmental' : 'physical');
  }, [isEnvRoute]);

  useEffect(() => {
    axios.get(`${API}/domain/sensors/override-options`, config)
      .then((r) => setOverrideOptions(r.data)).catch(() => {});
  }, []);

  const generate = async () => {
    try { await axios.post(`${API}/domain/generate`, {}, config); toast.success('Domain test data regenerated'); loadAll(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const ack = async (id) => {
    try { await axios.patch(`${API}/domain/alerts/${id}/acknowledge`, {}, config); toast.success('Acknowledged'); loadAll(true); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const ackSensor = async (id, action) => {
    try { await axios.patch(`${API}/domain/sensors/${id}?action=${action}`, {}, config); toast.success(action === 'reopen' ? 'Alarm re-opened' : 'Alarm cleared'); loadAll(true); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const overrideSensor = async (id, state) => {
    try { await axios.patch(`${API}/domain/sensors/${id}/override?state=${encodeURIComponent(state)}`, {}, config); toast.success(`Sensor manually set to "${state}"`); loadAll(true); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const restoreSensor = async (id) => {
    try { await axios.patch(`${API}/domain/sensors/${id}/restore`, {}, config); toast.success('Restored to automated reading'); loadAll(true); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const resolveStructural = async (id, status) => {
    try { await axios.patch(`${API}/domain/structural/${id}?status=${encodeURIComponent(status)}`, {}, config); toast.success(`Marked ${status}`); loadAll(true); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const resolveIncident = async (id, status) => {
    try { await axios.patch(`${API}/domain/incidents/${id}?status=${encodeURIComponent(status)}`, {}, config); toast.success(`Marked ${status}`); loadAll(true); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const exportCsv = () => {
    const rows = [['Site', 'Group', 'Sensor', 'Status', 'Value']].concat(
      sensors.map((s) => [s.asset_name, s.group, s.name, s.status, s.value ?? '']));
    const csv = rows.map((r) => r.map((x) => `"${x}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a'); a.href = url; a.download = 'domain_sensors.csv'; a.click();
  };
  const logout = () => { localStorage.clear(); navigate('/login'); };

  const groups = [
    { g: 'Door/Hatch', label: 'Door/Hatch Sensors' }, { g: 'Lock', label: 'Lock Status' },
    { g: 'Camera', label: 'CCTV Cameras' }, { g: 'Vibration', label: 'Vibration/Shock' },
    { g: 'Tilt', label: 'Tilt (Mobile)' }, { g: 'GPS/Motion', label: 'GPS/Motion (Mobile)' },
    { g: 'Geofence', label: 'Geofence (Mobile)' },
  ];

  const visibleTabs = TABS;

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      <AppSidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)}><Menu className="w-5 h-5" /></Button>
              <div>
                <h1 className="text-2xl font-bold text-black flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <Lock className="w-6 h-6 text-red-600" /> Domain Monitoring
                </h1>
                <p className="text-sm text-gray-500">Physical Security · Structural Integrity · Environmental Early-Warning</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => loadAll()} data-testid="domain-refresh"><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
              <Button variant="outline" size="sm" onClick={exportCsv}><FileDown className="w-4 h-4 mr-1" />CSV</Button>
              {!isGuest && (
                <Button size="sm" className="bg-gradient-to-r from-red-500 to-rose-600 text-white" onClick={generate} data-testid="domain-generate">
                  <Play className="w-4 h-4 mr-1" />Generate Test Data
                </Button>
              )}
            </div>
          </div>
          {/* KPIs */}
          {summary && (
            <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3">
              <KpiSmall icon={Eye} label="Physical Security" value={summary.physical_security} />
              <KpiSmall icon={Home} label="Structural Damage" value={summary.structural_damage} />
              <KpiSmall icon={Leaf} label="Env. Risk Level" value={summary.env_risk_level} valueColor={summary.env_risk_level === 'Critical' ? 'text-red-600' : summary.env_risk_level === 'Warning' ? 'text-orange-600' : 'text-green-600'} />
              <KpiSmall icon={AlertTriangle} label="Total Critical" value={summary.total_critical} valueColor="text-red-600" />
              <KpiSmall icon={Wifi} label="Cross-Domain Incidents" value={summary.cross_domain_incidents} />
            </div>
          )}
        </header>

        <div className="bg-white border-b border-gray-200 px-4 flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`domain-tab-${t.key}`}
              className={`px-3 py-3 text-sm font-medium whitespace-nowrap border-b-2 flex items-center gap-1.5
                ${tab === t.key ? 'border-red-600 text-red-700' : 'border-transparent text-gray-500 hover:text-black'}`}>
              <t.icon className="w-4 h-4" />{t.label}{t.key === 'incidents' && summary ? ` (${summary.cross_domain_incidents})` : ''}
            </button>
          ))}
        </div>

        <main className="flex-1 overflow-y-auto p-6">
          {loading ? <div className="flex items-center justify-center h-64 text-gray-400">Loading domain telemetry…</div> : (
            <>
              {tab === 'physical' && (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                  {groups.map(({ g, label }) => {
                    const items = sensors.filter((s) => s.group === g);
                    if (items.length === 0) return null;
                    return (
                      <Card key={g} className="bg-white border-gray-200" data-testid={`sensor-group-${g}`}>
                        <CardHeader className="pb-2"><CardTitle className="text-black text-base flex justify-between">{label}<span className="text-gray-400 text-sm">{items.length}</span></CardTitle></CardHeader>
                        <CardContent className="space-y-1.5 max-h-72 overflow-y-auto">
                          {items.map((s) => {
                            const opts = overrideOptions[s.group] || [];
                            return (
                            <div key={s.id} className="flex items-center justify-between gap-2 text-sm border-b border-gray-50 py-1.5">
                              <span className="text-gray-700 truncate min-w-0">{s.name} <span className="text-gray-400 text-xs">({s.asset_name.replace('BESS-', '')})</span></span>
                              <span className="flex items-center gap-1.5 shrink-0">
                                {['Vibration', 'Tilt'].includes(g)
                                  ? <span className={`text-xs font-semibold ${s.alarm ? 'text-red-600' : 'text-green-600'}`}>{s.status}</span>
                                  : <span className={statusPill(s.status)}>{s.status}</span>}
                                {s.overridden && (
                                  <span title={`Manually overridden${s.overridden_by ? ' by ' + s.overridden_by : ''}`}
                                    className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-300 flex items-center gap-0.5">
                                    <Wrench className="w-2.5 h-2.5" />Override
                                  </span>
                                )}
                                {s.acknowledged && !s.alarm && !s.overridden && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700 border border-green-200 flex items-center gap-0.5">
                                    <CheckCircle2 className="w-2.5 h-2.5" />Cleared
                                  </span>
                                )}
                                {!isGuest && s.alarm && !s.overridden && (
                                  <button
                                    onClick={() => ackSensor(s.id, 'acknowledge')}
                                    data-testid={`sensor-ack-${s.id}`}
                                    title="Acknowledge & clear this alarm"
                                    className="text-[11px] px-2 py-0.5 rounded bg-red-600 text-white hover:bg-red-700 transition-colors"
                                  >Clear</button>
                                )}
                                {!isGuest && s.acknowledged && !s.alarm && !s.overridden && (
                                  <button
                                    onClick={() => ackSensor(s.id, 'reopen')}
                                    data-testid={`sensor-reopen-${s.id}`}
                                    title="Re-open this alarm"
                                    className="text-[11px] px-2 py-0.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors flex items-center gap-0.5"
                                  ><RotateCcw className="w-2.5 h-2.5" />Reopen</button>
                                )}
                                {!isGuest && s.overridden && (
                                  <button
                                    onClick={() => restoreSensor(s.id)}
                                    data-testid={`sensor-restore-${s.id}`}
                                    title="Discard manual override and restore automated reading"
                                    className="text-[11px] px-2 py-0.5 rounded border border-amber-300 text-amber-700 hover:bg-amber-50 transition-colors flex items-center gap-0.5"
                                  ><RotateCcw className="w-2.5 h-2.5" />Restore Auto</button>
                                )}
                                {!isGuest && opts.length > 0 && (
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <button
                                        data-testid={`sensor-override-trigger-${s.id}`}
                                        title="Manually override sensor state"
                                        className="text-gray-400 hover:text-gray-700 transition-colors p-0.5"
                                      ><SlidersHorizontal className="w-3.5 h-3.5" /></button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-44">
                                      <DropdownMenuLabel className="text-xs">Manual Override</DropdownMenuLabel>
                                      <DropdownMenuSeparator />
                                      {opts.map((opt) => (
                                        <DropdownMenuItem
                                          key={opt}
                                          data-testid={`sensor-override-${s.id}-${opt}`}
                                          disabled={s.status === opt && s.overridden}
                                          onClick={() => overrideSensor(s.id, opt)}
                                          className="text-xs cursor-pointer"
                                        >Force: {opt}</DropdownMenuItem>
                                      ))}
                                      {s.overridden && (
                                        <>
                                          <DropdownMenuSeparator />
                                          <DropdownMenuItem
                                            data-testid={`sensor-override-restore-${s.id}`}
                                            onClick={() => restoreSensor(s.id)}
                                            className="text-xs cursor-pointer text-amber-700"
                                          ><RotateCcw className="w-3 h-3 mr-1" />Restore Auto</DropdownMenuItem>
                                        </>
                                      )}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                )}
                              </span>
                            </div>
                            );
                          })}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}

              {tab === 'damage' && (
                <Card className="bg-white border-gray-200">
                  <CardHeader><CardTitle className="text-black text-lg">Structural Integrity & Physical Damage ({structural.length})</CardTitle></CardHeader>
                  <CardContent className="space-y-2">
                    {structural.length === 0 && <p className="text-gray-400 text-sm">No structural damage detected.</p>}
                    {structural.map((s) => (
                      <div key={s.id} className="flex items-center justify-between gap-3 p-3 border border-gray-100 rounded-lg flex-wrap">
                        <div><div className="text-sm font-medium text-black">{s.type} — {s.asset_name}</div>
                          <div className="text-xs text-gray-500">{s.chemistry} · severity index {s.value} · {new Date(s.timestamp).toLocaleString()}</div></div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-0.5 rounded ${LEVEL_STYLE[s.severity]}`}>{s.severity}</span>
                          <span className={`text-xs px-2 py-0.5 rounded font-medium ${s.status === 'Resolved' ? 'bg-green-100 text-green-700' : s.status === 'Acknowledged' ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'}`}>{s.status}</span>
                          {!isGuest && s.status === 'Open' && (
                            <button onClick={() => resolveStructural(s.id, 'Acknowledged')} data-testid={`structural-ack-${s.id}`} className="text-[11px] px-2 py-0.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-100">Acknowledge</button>
                          )}
                          {!isGuest && s.status !== 'Resolved' && (
                            <button onClick={() => resolveStructural(s.id, 'Resolved')} className="text-[11px] px-2 py-0.5 rounded bg-red-600 text-white hover:bg-red-700">Resolve</button>
                          )}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {tab === 'environmental' && <EnvironmentalTab summary={summary} env={env} ack={ack} isGuest={isGuest} />}

              {tab === 'incidents' && (
                <div className="space-y-3">
                  {incidents.length === 0 && <p className="text-gray-400 text-sm">No cross-domain incidents.</p>}
                  {incidents.map((i) => (
                    <Card key={i.id} className={`bg-white border-l-4 ${i.severity === 'Critical' ? 'border-l-red-600' : 'border-l-orange-500'} border-gray-200`}>
                      <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
                        <div><div className="flex items-center gap-2"><span className={`text-xs px-2 py-0.5 rounded ${LEVEL_STYLE[i.severity]}`}>{i.severity}</span><span className="text-sm font-semibold text-black">{i.title}</span></div>
                          <div className="text-xs text-gray-500 mt-1">{i.asset_name} · Domains: {i.domains?.join(' + ')} · {new Date(i.opened_at).toLocaleString()}</div></div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-1 rounded font-medium ${i.status === 'Resolved' ? 'bg-green-100 text-green-700' : i.status === 'Investigating' ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'}`}>{i.status}</span>
                          {!isGuest && i.status === 'Open' && (
                            <button onClick={() => resolveIncident(i.id, 'Investigating')} data-testid={`incident-act-${i.id}`} className="text-[11px] px-2 py-0.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-100">Investigate</button>
                          )}
                          {!isGuest && i.status !== 'Resolved' && (
                            <button onClick={() => resolveIncident(i.id, 'Resolved')} className="text-[11px] px-2 py-0.5 rounded bg-red-600 text-white hover:bg-red-700">Resolve</button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {tab === 'compliance' && (
                <Card className="bg-white border-gray-200">
                  <CardHeader><CardTitle className="text-black text-lg">Physical & Environmental Compliance (NFPA 855 · UL 9540A · IEC 62619 · ISO 27001)</CardTitle></CardHeader>
                  <CardContent className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="text-left text-gray-500 border-b border-gray-200"><th className="py-2">Framework</th><th>Control</th><th>Score</th><th>Status</th><th>Last Assessed</th></tr></thead>
                      <tbody>
                        {compliance.map((c) => (
                          <tr key={c.id} className="border-b border-gray-100">
                            <td className="py-2"><Badge variant="outline" className="text-xs">{c.framework}</Badge></td>
                            <td className="text-gray-800">{c.control}</td><td className="font-semibold">{c.score}%</td>
                            <td><span className={`text-xs px-2 py-0.5 rounded ${c.status === 'Pass' ? 'bg-green-100 text-green-700' : c.status === 'Partial' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>{c.status}</span></td>
                            <td className="text-gray-500">{new Date(c.last_assessed).toLocaleDateString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}

const KpiSmall = ({ icon: Icon, label, value, valueColor = 'text-black' }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-3 flex items-center gap-3">
    <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center"><Icon className="w-5 h-5 text-red-600" /></div>
    <div><div className={`text-2xl font-bold ${valueColor}`} style={{ fontFamily: 'Manrope, sans-serif' }}>{value}</div><div className="text-xs text-gray-500">{label}</div></div>
  </div>
);

function EnvironmentalTab({ summary, env, ack, isGuest }) {
  const ov = summary?.env_overview || {};
  const ladder = summary?.early_warning_ladder || [];
  const open = (env.alerts || []).filter((a) => a.status === 'Open');
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiSmall icon={Leaf} label="Normal" value={ov.Normal || 0} valueColor="text-green-600" />
        <KpiSmall icon={Activity} label="Anomaly" value={ov.Anomaly || 0} valueColor="text-yellow-600" />
        <KpiSmall icon={AlertTriangle} label="Warning" value={ov.Warning || 0} valueColor="text-orange-600" />
        <KpiSmall icon={AlertTriangle} label="Critical" value={ov.Critical || 0} valueColor="text-red-600" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-white border-gray-200">
          <CardHeader><CardTitle className="text-black text-lg">Fleet Early-Warning Ladder</CardTitle></CardHeader>
          <CardContent className="space-y-2 max-h-[460px] overflow-y-auto">
            {ladder.length === 0 && <p className="text-gray-400 text-sm">All sites nominal.</p>}
            {ladder.map((l, i) => (
              <div key={l.site || i} className="flex items-center justify-between text-sm border-b border-gray-50 pb-1.5">
                <span className="flex items-center gap-2 text-gray-800">
                  <span className={`w-2.5 h-2.5 rounded-full ${l.level === 'Critical' ? 'bg-red-600' : l.level === 'Warning' ? 'bg-orange-500' : 'bg-yellow-400'}`} />
                  {l.site} <span className="text-xs text-gray-400">({l.chemistry})</span>
                </span>
                <span className="flex items-center gap-2">
                  <span className="text-xs text-gray-400">{l.signals} signals</span>
                  <span className={`text-xs px-2 py-0.5 rounded ${LEVEL_STYLE[l.level]}`}>{l.level}</span>
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200">
          <CardHeader><CardTitle className="text-black text-lg">Open Alerts ({open.length})</CardTitle></CardHeader>
          <CardContent className="space-y-2 max-h-[460px] overflow-y-auto">
            {open.length === 0 && <p className="text-gray-400 text-sm">No open environmental alerts.</p>}
            {open.map((a) => (
              <div key={a.id} className="p-3 border border-gray-100 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className={`text-xs px-2 py-0.5 rounded ${LEVEL_STYLE[a.severity]}`}>{a.severity}</span>
                  {!isGuest && <Button size="sm" variant="outline" onClick={() => ack(a.id)}>Acknowledge</Button>}
                </div>
                <div className="text-sm font-medium text-black mt-1">{a.title}</div>
                <div className="text-xs text-gray-600 mt-0.5">{a.detail}</div>
                <div className="text-[11px] text-gray-400 mt-0.5">{new Date(a.timestamp).toLocaleString()} · SLA {new Date(a.sla).toLocaleString()}</div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
